import React, { useState } from 'react';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Textarea } from '@/shared/components/ui/Textarea';
import { Select } from '@/shared/components/ui/Select';
import { DeleteConfirmationModal, Tooltip } from '@/shared/components/ui';
import {
  useStagesByPipelineId,
  useCreateStage,
  useUpdateStage,
  useDeleteStageWithValidation,
} from '../hooks/useStages';
import type { TicketStageResponse, TicketStatusCategory } from '@troia-v3/types';
import { TICKET_STATUS_CATEGORIES } from '@troia-v3/types';
import { Icon } from '@/shared/icons';

/**
 * Gerenciador de etapas de um pipeline — formulário de criação/edição + lista
 * reordenável por arrasto.
 *
 * Extraído da antiga modal de configuração (view `manage-stages`, já
 * removida) para virar a aba "Etapas" do `PipelinesSettingsDrawer`. Comportamento
 * preservado região a região; a diferença é que NÃO existe mais `onBack`: a
 * lista de pipelines fica sempre visível à esquerda no master-detail, então o
 * botão "Voltar para Lista de Pipelines" (:849-851) não tem função.
 *
 * O `Select` de categoria é a peça crítica: `TICKET_STATUS_CATEGORIES` carrega
 * `slaBehavior` por categoria, e é ele que o motor de SLA lê para decidir
 * pausa/parada de relógio. A legenda "Controla o comportamento do SLA" segue
 * literal, e deixa de ser promessa quando o motor entra.
 */
export interface StagesManagerProps {
  pipelineId: string;
}

const CATEGORY_OPTIONS = Object.entries(TICKET_STATUS_CATEGORIES).map(([key, config]) => ({
  value: key,
  label: config.label,
}));

const EMPTY_STAGE_FORM = {
  name: '',
  description: '',
  color: '#8b5cf6',
  category: 'new' as TicketStatusCategory,
};

/**
 * Nova ordem dos ids depois de arrastar `draggedId` para a posição de
 * `targetId`. `null` = arrasto sem efeito (mesmo item, id fora da lista, id
 * vazio) — nesse caso o chamador não dispara nenhum PUT.
 *
 * Mesma aritmética do `handleDrop` da antiga modal (já removida),
 * só que sobre ids e sem I/O, para poder ser testada por unidade.
 */
export function reorderStageIds(
  stages: ReadonlyArray<{ id: string }>,
  draggedId: string,
  targetId: string,
): string[] | null {
  if (!draggedId || draggedId === targetId) return null;
  const draggedIdx = stages.findIndex((s) => s.id === draggedId);
  const targetIdx = stages.findIndex((s) => s.id === targetId);
  if (draggedIdx === -1 || targetIdx === -1) return null;

  const ids = stages.map((s) => s.id);
  const [moved] = ids.splice(draggedIdx, 1);
  ids.splice(targetIdx, 0, moved);
  return ids;
}

export const StagesManager: React.FC<StagesManagerProps> = ({ pipelineId }) => {
  const [stageForm, setStageForm] = useState(EMPTY_STAGE_FORM);
  const [editingStage, setEditingStage] = useState<TicketStageResponse | null>(null);
  const [stageToDelete, setStageToDelete] = useState<TicketStageResponse | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const { data: stages = [], isLoading } = useStagesByPipelineId(pipelineId);
  const createStage = useCreateStage();
  const updateStage = useUpdateStage();
  const deleteStage = useDeleteStageWithValidation();

  const handleCreateStage = async () => {
    try {
      await createStage.mutateAsync({
        pipelineId,
        name: stageForm.name,
        description: stageForm.description,
        color: stageForm.color,
        category: stageForm.category,
        order: stages.length,
      });
      setStageForm(EMPTY_STAGE_FORM);
    } catch {
      // toast tratado pelo hook (`useCreateStage.onError`, useStages.ts)
    }
  };

  const handleUpdateStage = async () => {
    if (!editingStage) return;
    try {
      await updateStage.mutateAsync({
        id: editingStage.id,
        data: {
          name: stageForm.name,
          description: stageForm.description,
          color: stageForm.color,
          category: stageForm.category,
        },
      });
      setStageForm(EMPTY_STAGE_FORM);
      setEditingStage(null);
    } catch {
      // toast tratado pelo hook (`useUpdateStage.onError`, useStages.ts)
    }
  };

  const handleStartEdit = (stage: TicketStageResponse) => {
    setEditingStage(stage);
    setStageForm({
      name: stage.name,
      description: stage.description || '',
      color: stage.color || '#8b5cf6',
      category: (stage.category as TicketStatusCategory) || 'new',
    });
  };

  const handleCancelEdit = () => {
    setEditingStage(null);
    setStageForm(EMPTY_STAGE_FORM);
  };

  const handleDeleteStage = (stage: TicketStageResponse) => {
    setStageToDelete(stage);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!stageToDelete) return;
    try {
      await deleteStage.mutateAsync(stageToDelete.id);
      setIsDeleteModalOpen(false);
      setStageToDelete(null);
    } catch {
      // toast tratado pelo hook (`useDeleteStageWithValidation.onError`, useStages.ts)
    }
  };

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, stageId: string) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', stageId);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>, targetId: string) => {
    e.preventDefault();
    const ordered = reorderStageIds(stages, e.dataTransfer.getData('text/plain'), targetId);
    if (!ordered) return;
    try {
      await Promise.all(
        ordered.map((id, index) => updateStage.mutateAsync({ id, data: { order: index } })),
      );
    } catch {
      // toast tratado pelo hook
    }
  };

  return (
    <div className="space-y-6">
      {/* Formulário */}
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-2" style={{ color: '#1e293b' }}>
            Nome da Etapa
          </label>
          <Input
            type="text"
            value={stageForm.name}
            onChange={(e) => setStageForm({ ...stageForm, name: e.target.value })}
            placeholder="Ex: Triagem, Em Desenvolvimento, Aguardando Deploy"
            data-testid="stages-manager-name"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            {/* Select shared com prop `options` — NUNCA <option> como children
                (a interface tipa `children` como never). */}
            <Select
              label="Categoria"
              value={stageForm.category}
              onChange={(value) => setStageForm({ ...stageForm, category: value as TicketStatusCategory })}
              options={CATEGORY_OPTIONS}
              data-testid="stages-manager-category"
            />
            <p className="text-xs mt-1" style={{ color: '#64748b' }}>
              Controla o comportamento do SLA
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#1e293b' }}>
              Cor
            </label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={stageForm.color}
                onChange={(e) => setStageForm({ ...stageForm, color: e.target.value })}
                className="w-10 h-10 rounded cursor-pointer border"
                style={{ borderColor: '#d1d5db' }}
                data-testid="stages-manager-color-picker"
              />
              <Input
                type="text"
                value={stageForm.color}
                onChange={(e) => setStageForm({ ...stageForm, color: e.target.value })}
                className="font-mono text-sm"
                placeholder="#8b5cf6"
                data-testid="stages-manager-color-hex"
              />
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2" style={{ color: '#1e293b' }}>
            Descrição (Opcional)
          </label>
          <Textarea
            value={stageForm.description}
            onChange={(e) => setStageForm({ ...stageForm, description: e.target.value })}
            rows={2}
            resize="none"
            placeholder="Descreva esta etapa"
            data-testid="stages-manager-description"
          />
        </div>

        <div className="flex gap-2">
          <Button
            onClick={editingStage ? handleUpdateStage : handleCreateStage}
            variant="primary"
            disabled={!stageForm.name.trim()}
            className="flex-1"
            data-testid="stages-manager-submit"
          >
            {editingStage ? 'Salvar Alterações' : 'Adicionar Etapa'}
          </Button>
          {editingStage && (
            <Button onClick={handleCancelEdit} variant="secondary" data-testid="stages-manager-cancel-edit">
              Cancelar
            </Button>
          )}
        </div>
      </div>

      {/* Lista de etapas */}
      <div>
        <h3 className="text-sm font-medium mb-3" style={{ color: '#1e293b' }}>
          Etapas Cadastradas ({stages.length})
        </h3>

        {isLoading ? (
          <div className="text-center py-4" style={{ color: '#64748b' }}>
            Carregando etapas...
          </div>
        ) : stages.length === 0 ? (
          <div className="text-center py-4" style={{ color: '#64748b' }}>
            Nenhuma etapa cadastrada ainda.
          </div>
        ) : (
          <div className="space-y-2">
            {stages.map((stage) => {
              const categoryConfig = TICKET_STATUS_CATEGORIES[stage.category as TicketStatusCategory];
              return (
                <div
                  key={stage.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, stage.id)}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, stage.id)}
                  className="flex items-center gap-3 p-3 rounded border transition-colors"
                  style={{
                    borderColor: editingStage?.id === stage.id ? '#8b5cf6' : '#e5e7eb',
                    backgroundColor: editingStage?.id === stage.id ? '#f1f5f9' : 'transparent',
                    cursor: 'grab',
                  }}
                  data-testid={`stages-manager-row-${stage.id}`}
                >
                  <Icon name="drag" size={16} style={{ color: '#64748b', cursor: 'grab' }} />
                  <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: stage.color }} />
                  <div className="flex-1">
                    <div className="font-medium" style={{ color: '#1e293b' }}>{stage.name}</div>
                    {stage.description && (
                      <div className="text-sm" style={{ color: '#64748b' }}>{stage.description}</div>
                    )}
                  </div>
                  {categoryConfig && (
                    <span
                      className="text-xs px-2 py-0.5 rounded-full"
                      style={{
                        backgroundColor: `${categoryConfig.color}15`,
                        color: categoryConfig.color,
                      }}
                    >
                      {categoryConfig.label}
                    </span>
                  )}
                  <div className="flex items-center gap-1">
                    <Tooltip content="Editar etapa">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleStartEdit(stage); }}
                        onMouseDown={(e) => e.stopPropagation()}
                        className="p-1.5 rounded transition-colors"
                        style={{ color: '#1e293b', cursor: 'pointer' }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                        data-testid={`pipelines-stage-edit-${stage.id}`}
                      >
                        <Icon name="edit" size={16} />
                      </button>
                    </Tooltip>
                    <Tooltip content="Excluir etapa">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteStage(stage); }}
                        onMouseDown={(e) => e.stopPropagation()}
                        className="p-1.5 rounded transition-colors"
                        style={{ color: '#ef4444', cursor: 'pointer' }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(239,68,68,0.1)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                        data-testid={`pipelines-stage-delete-${stage.id}`}
                      >
                        <Icon name="trash" size={16} />
                      </button>
                    </Tooltip>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Confirmação de exclusão — o aviso é o que a antiga modal já dizia
          (já removida), com acentuação corrigida. */}
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setStageToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Excluir Etapa"
        message={`Tem certeza que deseja excluir a etapa "${stageToDelete?.name}"?`}
        warningMessage="Excluir esta etapa moverá os tickets para a etapa padrão. Esta ação não pode ser desfeita."
        isLoading={deleteStage.isPending}
        confirmButtonText="Excluir Etapa"
      />
    </div>
  );
};

StagesManager.displayName = 'StagesManager';
